<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="visible" class="connection-error-overlay">
        <div class="connection-error-modal">
          <!-- Warning Icon -->
          <div class="connection-error-modal__icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 9V13M12 17H12.01M12 3L2 21H22L12 3Z" 
                    stroke="currentColor" 
                    stroke-width="2" 
                    stroke-linecap="round" 
                    stroke-linejoin="round"/>
            </svg>
          </div>

          <!-- Title -->
          <h2 class="connection-error-modal__title">
            {{ $t('errors.connectionLost') }}
          </h2>

          <!-- Message -->
          <p class="connection-error-modal__message">
            {{ $t('errors.connectionLostMessage') }}
          </p>

          <!-- Refresh Button -->
          <button class="connection-error-modal__btn" @click="handleRefresh">
            <svg class="connection-error-modal__btn-icon" viewBox="0 0 24 24" fill="none">
              <path d="M4 4V9H4.58152M19.9381 11C19.446 7.05369 16.0796 4 12 4C8.64262 4 5.76829 6.06817 4.58152 9M4.58152 9H9M20 20V15H19.4185M19.4185 15C18.2317 17.9318 15.3574 20 12 20C7.92038 20 4.55399 16.9463 4.06189 13M19.4185 15H15" 
                    stroke="currentColor" 
                    stroke-width="2" 
                    stroke-linecap="round" 
                    stroke-linejoin="round"/>
            </svg>
            {{ $t('errors.refreshPage') }}
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
defineProps<{
  visible: boolean
}>()

function handleRefresh() {
  window.location.reload()
}
</script>

<style scoped>
.connection-error-overlay {
  position: fixed;
  inset: 0;
  background: var(--ds-scrim-strong);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  padding: 16px;
}

.connection-error-modal {
  background: linear-gradient(180deg, var(--hm-surface-alt) 0%, var(--hm-surface) 100%);
  border-radius: var(--ds-radius-modal);
  padding: 32px 28px;
  max-width: 360px;
  width: 100%;
  text-align: center;
  box-shadow: 
    0 4px 24px var(--ds-scrim-soft),
    0 0 0 1px var(--ds-overlay-06);
  animation: modalBounce 0.3s ease-out;
}

@keyframes modalBounce {
  0% {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  70% {
    transform: scale(1.02) translateY(-2px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.connection-error-modal__icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  background: var(--ds-warning-soft);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.connection-error-modal__icon svg {
  width: 32px;
  height: 32px;
  color: var(--hm-warning);
}

.connection-error-modal__title {
  font-size: var(--ds-text-size-h-sm);
  font-weight: var(--ds-weight-semibold);
  color: var(--hm-text-primary);
  margin: 0 0 12px;
  letter-spacing: -0.02em;
}

.connection-error-modal__message {
  font-size: var(--ds-text-size-body);
  line-height: 1.5;
  color: var(--ds-overlay-60);
  margin: 0 0 28px;
}

.connection-error-modal__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 24px;
  font-size: var(--p-text-15);
  font-weight: var(--ds-weight-semibold);
  color: var(--hm-text-primary);
  background: linear-gradient(135deg, var(--hm-accent) 0%, var(--hm-accent-dark) 100%);
  border: none;
  border-radius: var(--p-radius-50);
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px var(--ds-accent-soft-30);
}

.connection-error-modal__btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px var(--ds-accent-soft-40);
}

.connection-error-modal__btn:active {
  transform: translateY(0);
}

.connection-error-modal__btn-icon {
  width: 18px;
  height: 18px;
}

/* Transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
